import { useState, useEffect } from 'react'
import { getDataAPI, deleteDataAPI, postDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'

interface User {
  id: string
  email: string
  role: string
  created_at: string
}

export default function PeoplePage() {
  const [users, setUsers] = useState<User[]>([])
  const [_isLoading, setIsLoading] = useState(true)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [inviteForm, setInviteForm] = useState({
    email: '',
    password: '',
    role: 'readonly',
  })

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getDataAPI('users')
        setUsers(data || [])
      } catch (error) {
        console.error('Error fetching users:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchUsers()
  }, [])

  const refresh = async () => {
    const data = await getDataAPI('users').catch(() => [])
    setUsers(data || [])
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      // Backend route may differ; this matches our current API pattern.
      await postDataAPI('users/invite', inviteForm)
      setIsInviteOpen(false)
      setInviteForm({ email: '', password: '', role: 'readonly' })
      await refresh()
    } catch (error) {
      console.error('Failed to invite user:', error)
      alert('Failed to invite user. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemoveUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this user?')) return
    
    try {
      await deleteDataAPI(`users/${userId}`)
      setUsers(users.filter(u => u.id !== userId))
    } catch (error) {
      console.error('Error removing user:', error)
      alert('Failed to remove user')
    }
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-10 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2">People / Access</h1>
          <p className="text-sm text-gray-500">Control access, not communication</p>
        </div>
        <Button onClick={() => setIsInviteOpen(true)}>Invite User</Button>
      </div>

      <Modal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        title="Invite User"
        maxWidthClassName="max-w-md"
      >
        <form onSubmit={handleInvite} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              required
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <input
              type="password"
              required
              value={inviteForm.password}
              onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Role</label>
            <select
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
              className="ds-input"
            >
              <option value="owner">Owner</option>
              <option value="manager">Manager</option>
              <option value="assistant">Assistant</option>
              <option value="readonly">Read-only</option>
            </select>
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Inviting…' : 'Invite'}
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsInviteOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {users.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-10">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Email</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Role</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Created</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="py-3 px-4 text-sm text-gray-900">{user.email}</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700">
                      {user.role}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{new Date(user.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleRemoveUser(user.id)}
                      className="text-sm text-red-600 hover:text-red-700 font-medium transition-colors"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center mb-10">
          <p className="text-sm text-gray-500">No users found</p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-sm font-medium mb-4 text-gray-900">Roles</h2>
        <ul className="space-y-3 text-sm text-gray-600">
          <li><span className="font-medium text-gray-900">Owner:</span> Full access to all sections</li>
          <li><span className="font-medium text-gray-900">Manager:</span> Access to most sections, limited settings</li>
          <li><span className="font-medium text-gray-900">Assistant:</span> Read and edit access to operational sections</li>
          <li><span className="font-medium text-gray-900">Read-only:</span> View-only access to all sections</li>
        </ul>
        <p className="mt-4 text-xs text-gray-500">
          TODO: Section-level permissions will be implemented as requirements are provided.
        </p>
      </div>
    </div>
  )
}
